import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MessageCircle, X, Send, Bot, User, Paperclip, Image, FileText, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";

interface Message {
  id: string;
  content: string;
  role: "user" | "assistant" | "support";
  created_at: string;
  attachment_url?: string | null;
  attachment_type?: string | null;
  attachment_name?: string | null;
}

const LiveChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Check auth and load/create conversation
  useEffect(() => {
    let isMounted = true;

    const checkAuthAndLoadChat = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!isMounted) return;

      if (user) {
        setUserId(user.id);
        await loadOrCreateConversation(user.id);
      }
    };

    void checkAuthAndLoadChat();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user) {
        setUserId(session.user.id);
        setTimeout(() => {
          if (isMounted) {
            void loadOrCreateConversation(session.user.id);
          }
        }, 0);
      } else {
        setUserId(null);
        setConversationId(null);
        setMessages([]);
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Subscribe to realtime messages
  useEffect(() => {
    if (!conversationId) return;

    const channel = supabase
      .channel(`chat-${conversationId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `conversation_id=eq.${conversationId}`,
        },
        (payload) => {
          const newMessage = payload.new as Message;
          setMessages((prev) => {
            // Avoid duplicates
            if (prev.find(m => m.id === newMessage.id)) return prev;
            return [...prev, newMessage];
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [conversationId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const loadOrCreateConversation = async (uid: string) => {
    setIsLoading(true);
    try {
      // Try to find existing open conversation
      const { data: existingConvo } = await supabase
        .from('chat_conversations')
        .select('id')
        .eq('user_id', uid)
        .eq('status', 'open')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (existingConvo) {
        setConversationId(existingConvo.id);
        await loadMessages(existingConvo.id);
      } else {
        // Create new conversation
        const { data: newConvo } = await supabase
          .from('chat_conversations')
          .insert({ user_id: uid })
          .select('id')
          .single();

        if (newConvo) {
          setConversationId(newConvo.id);
          // Add welcome message
          setMessages([{
            id: 'welcome',
            content: "Hello! Welcome to Lecando Swed support. How can we help you today?",
            role: "assistant",
            created_at: new Date().toISOString(),
          }]);
        }
      }
    } catch (error) {
      console.error("Failed to load support chat:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadMessages = async (convoId: string) => {
    const { data } = await supabase
      .from('chat_messages')
      .select('*')
      .eq('conversation_id', convoId)
      .order('created_at', { ascending: true });

    if (data && data.length > 0) {
      setMessages(data as Message[]);
    } else {
      // No messages yet, show welcome
      setMessages([{
        id: 'welcome',
        content: "Hello! Welcome to Lecando Swed support. How can we help you today?",
        role: "assistant",
        created_at: new Date().toISOString(),
      }]);
    }
  };

  const uploadFile = async (file: File): Promise<{ url: string; type: string; name: string; path: string } | null> => {
    if (!userId) return null;
    
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}.${fileExt}`;
    const filePath = `${userId}/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('chat-attachments')
      .upload(filePath, file);

    if (uploadError) {
      console.error('Upload error:', uploadError);
      return null;
    }

    // Use signed URL instead of public URL for private bucket
    const { data: signedUrlData, error: signedUrlError } = await supabase.storage
      .from('chat-attachments')
      .createSignedUrl(filePath, 60 * 60 * 24 * 7); // 7 days expiry

    if (signedUrlError || !signedUrlData?.signedUrl) {
      console.error('Signed URL error:', signedUrlError);
      return null;
    }

    const isImage = file.type.startsWith('image/');
    
    return {
      url: signedUrlData.signedUrl,
      type: isImage ? 'image' : 'file',
      name: file.name,
      path: filePath // Store path for regenerating signed URLs later
    };
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !conversationId || !userId) return;

    // Limit file size to 10MB
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }

    setIsUploading(true);

    const attachment = await uploadFile(file);
    if (!attachment) {
      setIsUploading(false);
      return;
    }

    // Send message with attachment
    const { error } = await supabase
      .from('chat_messages')
      .insert({
        conversation_id: conversationId,
        user_id: userId,
        role: 'user',
        content: input.trim() || `Sent ${attachment.type === 'image' ? 'an image' : 'a file'}: ${attachment.name}`,
        attachment_url: attachment.url,
        attachment_type: attachment.type,
        attachment_name: attachment.name,
      });

    if (error) {
      console.error('Error sending message:', error);
    } else {
      setInput("");
      await supabase
        .from('chat_conversations')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', conversationId);
    }

    setIsUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSend = async () => {
    if (!input.trim() || !conversationId || !userId) return;

    const content = input.trim();
    setInput("");
    setIsTyping(true);

    // Insert message to database
    const { error } = await supabase
      .from('chat_messages')
      .insert({
        conversation_id: conversationId,
        user_id: userId,
        role: 'user',
        content,
      });

    if (error) {
      console.error('Error sending message:', error);
      setIsTyping(false);
      return;
    }

    // Update conversation timestamp
    await supabase
      .from('chat_conversations')
      .update({ updated_at: new Date().toISOString() })
      .eq('id', conversationId);

    // Auto-reply for now (until support team responds)
    setTimeout(async () => {
      setIsTyping(false);
    }, 1500);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const renderAttachment = (message: Message) => {
    if (!message.attachment_url) return null;

    if (message.attachment_type === 'image') {
      return (
        <a href={message.attachment_url} target="_blank" rel="noopener noreferrer">
          <img 
            src={message.attachment_url} 
            alt={message.attachment_name || 'Attached image'} 
            className="max-w-[200px] rounded-lg mt-2 cursor-pointer hover:opacity-90 transition-opacity"
          />
        </a>
      );
    }

    return (
      <a 
        href={message.attachment_url} 
        target="_blank" 
        rel="noopener noreferrer"
        className="flex items-center gap-2 mt-2 p-2 bg-background/50 rounded-lg hover:bg-background/70 transition-colors"
      >
        <FileText className="h-4 w-4" />
        <span className="text-xs truncate max-w-[150px]">{message.attachment_name}</span>
      </a>
    );
  };

  // Don't show chat for non-authenticated users
  if (!userId) {
    return null;
  }

  return (
    <>
      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        className="hidden"
        accept="image/*,.pdf,.doc,.docx,.txt"
      />

      {/* Chat Button */}
      <Button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "fixed bottom-24 md:bottom-6 right-6 h-14 w-14 rounded-full shadow-lg z-50 transition-all duration-300",
          "bg-gradient-primary hover:opacity-90 hover:scale-110",
          isOpen && "rotate-90"
        )}
        size="icon"
      >
        {isOpen ? (
          <X className="h-6 w-6" />
        ) : (
          <MessageCircle className="h-6 w-6" />
        )}
      </Button>

      {/* Chat Window */}
      <div
        className={cn(
          "fixed bottom-[7.5rem] md:bottom-24 right-6 z-50 transition-all duration-300 transform",
          isOpen
            ? "opacity-100 translate-y-0 scale-100"
            : "opacity-0 translate-y-4 scale-95 pointer-events-none"
        )}
      >
        <Card className="w-[350px] md:w-[400px] shadow-2xl border-0 overflow-hidden">
          <CardHeader className="bg-gradient-primary text-primary-foreground p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-full">
                <Bot className="h-5 w-5" />
              </div>
              <div>
                <CardTitle className="text-lg">Lecando Support</CardTitle>
                <p className="text-sm opacity-90">We typically reply within hours</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[350px] p-4" ref={scrollRef}>
              {isLoading ? (
                <div className="flex items-center justify-center h-full">
                  <p className="text-muted-foreground">Loading chat...</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={cn(
                        "flex gap-2",
                        message.role === "user" ? "justify-end" : "justify-start"
                      )}
                    >
                      {(message.role === "assistant" || message.role === "support") && (
                        <div className={cn(
                          "p-1.5 rounded-full h-fit",
                          message.role === "support" ? "bg-accent/20" : "bg-primary/10"
                        )}>
                          <Bot className={cn(
                            "h-4 w-4",
                            message.role === "support" ? "text-accent" : "text-primary"
                          )} />
                        </div>
                      )}
                      <div
                        className={cn(
                          "max-w-[80%] rounded-2xl px-4 py-2.5 text-sm",
                          message.role === "user"
                            ? "bg-primary text-primary-foreground rounded-br-md"
                            : message.role === "support"
                            ? "bg-accent/10 text-foreground rounded-bl-md border border-accent/20"
                            : "bg-secondary text-secondary-foreground rounded-bl-md"
                        )}
                      >
                        {message.role === "support" && (
                          <p className="text-xs text-accent font-medium mb-1">Support Team</p>
                        )}
                        {message.content}
                        {renderAttachment(message)}
                      </div>
                      {message.role === "user" && (
                        <div className="p-1.5 bg-primary/10 rounded-full h-fit">
                          <User className="h-4 w-4 text-primary" />
                        </div>
                      )}
                    </div>
                  ))}
                  {isTyping && (
                    <div className="flex gap-2 justify-start">
                      <div className="p-1.5 bg-primary/10 rounded-full h-fit">
                        <Bot className="h-4 w-4 text-primary" />
                      </div>
                      <div className="bg-secondary rounded-2xl rounded-bl-md px-4 py-3">
                        <div className="flex gap-1">
                          <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                          <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                          <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </ScrollArea>
            <div className="p-4 border-t bg-background">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isLoading || isUploading}
                  className="shrink-0"
                >
                  {isUploading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Paperclip className="h-4 w-4" />
                  )}
                </Button>
                <Input
                  placeholder="Type your message..."
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="flex-1"
                  disabled={isLoading || isUploading}
                />
                <Button
                  onClick={handleSend}
                  disabled={!input.trim() || isTyping || isLoading || isUploading}
                  size="icon"
                  className="bg-gradient-primary hover:opacity-90"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default LiveChat;